import { selectCurrentHotelId, selectIsInternalAdmin } from 'accessors'
import Button from 'components/ReusableComponents/Button'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import NewBadge from 'components/ReusableComponents/NewBadge'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import CampaignHistory from 'features/CampaignHistory'
import {
  EmailEditorProvider
} from 'features/EmailEditor/EmailEditorContext'
import CampaignEmailVariantSection from 'features/ManageCampaigns/CampaignEmailVariantSection'
import MediaUploader from 'features/ManageCampaigns/MediaUploader'
import LockedEmailTemplatePreview from 'features/ManageCampaigns/LockedEmailTemplatePreview'
import { TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import {
  getExtensionFromUrl,
  getImgUrlFromCDN,
  isCampaignImageVisible
} from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useEsc from 'hooks/useEsc'
import useFirstRender from 'hooks/useFirstRender'
import usePlausible from 'hooks/usePlausible'
import { DEFAULT_SUBSIDIARY_ID } from 'main/globalVariables'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import campaignApi from 'services/api/campaign'

import CampaignCustomUrl from './CampaignCustomUrl'
import styles from './EditTextDialog.module.scss'
import TextArea from './TextArea'
import { TextPreview } from './TextPreview'
import { getCampaignTitle } from './utils/getCampaignTitle'
import { getCampaignName } from './utils/getCategoryName'
import {
  inviteContentTabs,
  shouldShowInviteEmailContentTab
} from './utils/inviteCampaignEditorHelpers'
import { getEmailCampaignVariants } from './utils/inviteEmailCampaignVariants'

const mediaTag = mediaTags.campaign

const urlRegex = /^(http|https)?:\/\/\S+/i

const EditCampaignContentInner = ({
  canBePublished,
  onSaveText,
  defaultData,
  hasOptOutText = false,
  hasLink = false,
  tags = [],
  requiredTagIds = [],
  optionalTagIds = ['__guestLastName__'],
  children,
  canHaveImages = false,
  category,
  campaignMedia: defaultCampaignMedia,
  hasReviewLinkControl,
  hasCustomLinkControl,
  campaigns = [],
  isDefaultCampaigns,
  customUrl,
  showEmailTab = false,
  activeContentTab = inviteContentTabs.text,
  setActiveContentTab,
  hasEmailService = false
}) => {
  const history = useHistory()
  const wrapperRef = useRef()
  const [emailPreviewPortalTarget, setEmailPreviewPortalTarget] = useState(null)
  const emailEditorsRef = useRef({})
  const { postEvent, plausibleEvents } = usePlausible()
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const hotelId = useSelector(selectCurrentHotelId)

  const [campaignsToEdit, setCampaignsToEdit] = useState(
    [...campaigns].map(c => ({
      ...c,
      remainingTags: [],
      customUrl: c.customUrl || null,
      messageTemplate: c.messageTemplate.replace(/[\r]/g, '')
    }))
  )
  const [isCustomLinkEnabled, setIsCustomLinkEnabled] = useState(!!customUrl)
  const [customLink, setCustomLink] = useState(customUrl)
  const [isSpamCheckDialogVisible, setIsSpamCheckDialogVisible] = useState(false)
  const [isLoadingSpamCheckResults, setIsLoadingSpamCheckResults] = useState(true)
  const [spamResults, setSpamResults] = useState({})
  const [focusedCampaignId, setFocusedCampaignId] = useState(campaignsToEdit[0].contentId)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [isEditHistoryVisible, setIsEditHistoryVisible] = useState(false)
  const [categoryToShowHistory, setCategoryToShowHistory] = useState('')
  const [contentIdToShowHistory, setContentIdToShowHistory] = useState()
  const [campaignMedia, setCampaignMedia] = useState(defaultCampaignMedia)

  const emailCampaignVariants = useMemo(() => getEmailCampaignVariants(campaigns), [campaigns])
  const [focusedEmailContentId, setFocusedEmailContentId] = useState(
    () => emailCampaignVariants[0]?.contentId
  )

  useEffect(() => {
    if (
      focusedEmailContentId &&
      emailCampaignVariants.some(campaign => campaign.contentId === focusedEmailContentId)
    ) {
      return
    }

    setFocusedEmailContentId(emailCampaignVariants[0]?.contentId)
  }, [emailCampaignVariants, focusedEmailContentId])

  const registerEmailEditor = useCallback((contentId, emailEditor) => {
    if (emailEditor) {
      emailEditorsRef.current[contentId] = emailEditor
      return
    }

    delete emailEditorsRef.current[contentId]
  }, [])

  async function saveEmailCampaignVariants() {
    const editors = Object.values(emailEditorsRef.current).filter(Boolean)
    const dirtyEditors = editors.filter(editor => editor.hasUnsavedChanges)

    await Promise.all(dirtyEditors.map(editor => editor.saveChanges()))
  }

  function hasUnsavedEmailChanges() {
    return Object.values(emailEditorsRef.current).some(
      editor => editor?.hasUnsavedChanges
    )
  }

  useFirstRender(() => {
    postEvent(plausibleEvents.ViewCampaignEditor, {
      category
    })
  }, [category, postEvent, plausibleEvents.ViewCampaignEditor])

  const title = `${showEmailTab ? 'Editing content for' : 'Editing text for'} ${getCampaignName(
    category,
    isInternalAdmin
  )}`

  function checkForSpam({ messageTemplate, hasMedia, category }) {
    setIsSpamCheckDialogVisible(true)
    setIsLoadingSpamCheckResults(true)

    campaignApi
      .spamCheck({
        proposedContent: messageTemplate,
        proposedHasMedia: hasMedia,
        category
      })
      .then(res => {
        setIsLoadingSpamCheckResults(false)
        setSpamResults(res)
      })
  }

  function handleClickShowHistory(category, contentId) {
    setCategoryToShowHistory(category)
    setContentIdToShowHistory(contentId)
    setIsEditHistoryVisible(true)
  }

  const isCustomLinkValid = customLink ? !!customLink.match(urlRegex) : false

  function handleChangeCustomLinkToggle(e) {
    setIsCustomLinkEnabled(e.target.checked)
    if (!e.target.check) {
      setCustomLink(null)
    }
  }

  useEsc(() => {
    if (wrapperRef.current.offsetParent !== null) {
      history.goBack()
    }
  })

  function handleCampaignChange(contentId, text) {
    const updatedCampaigns = campaignsToEdit.map(campaign => {
      if (campaign.contentId === contentId) {
        return {
          ...campaign,
          messageTemplate: text
        }
      }
      return campaign
    })

    setCampaignsToEdit(updatedCampaigns)
  }

  function handleUpdateCampaignTags(contentId, usedTags) {
    setCampaignsToEdit(existingCampaigns =>
      existingCampaigns.map(campaign => {
        if (campaign.contentId !== contentId) {
          return campaign
        }

        const nextRemainingTags = tags
          .filter(t => requiredTagIds.includes(t.id))
          .filter(t => !usedTags.find(u => u.id === t.id))

        const remainingTagIds = campaign.remainingTags.map(t => t.id).join(',')
        const nextRemainingTagIds = nextRemainingTags.map(t => t.id).join(',')

        if (remainingTagIds === nextRemainingTagIds) {
          return campaign
        }

        return {
          ...campaign,
          remainingTags: nextRemainingTags
        }
      })
    )
  }

  function persistSmsContent() {
    if (canHaveImages) {
      onSaveText(campaignsToEdit[0].messageTemplate, campaignMedia)
      return
    }

    if (isCustomLinkEnabled) {
      const campaignsWithCustomUrl = campaignsToEdit.map(campaign => ({
        ...campaign,
        customUrl: customLink
      }))
      onSaveText(campaignsWithCustomUrl)
      return
    }

    const campaignsWithoutCustomUrl = campaignsToEdit.map(campaign => ({
      ...campaign,
      customUrl: null
    }))
    onSaveText(campaignsWithoutCustomUrl)
  }

  async function saveContent() {
    const isSavingEmailContent =
      showEmailTab &&
      (activeContentTab === inviteContentTabs.email || hasUnsavedEmailChanges())

    if (isSavingEmailContent && promptEmailSubscriptionUpgrade()) {
      return
    }

    if (showEmailTab && activeContentTab === inviteContentTabs.email) {
      if (hasEmailService) {
        await saveEmailCampaignVariants()
      }
      history.goBack()
      return
    }

    if (campaignsToEdit.some(c => c.remainingTags.length > 0)) {
      setShowValidationErrors(true)
      return
    }

    if (isCustomLinkEnabled && !isCustomLinkValid) {
      setShowValidationErrors(true)
      return
    }

    if (showEmailTab && hasEmailService && hasUnsavedEmailChanges()) {
      await saveEmailCampaignVariants()
    }

    persistSmsContent()
  }

  function goToPublish(category) {
    if (campaignsToEdit.some(c => c.remainingTags.length > 0)) {
      setShowValidationErrors(true)
      return
    }

    if (isCustomLinkEnabled && !isCustomLinkValid) {
      setShowValidationErrors(true)
      return
    }

    const details = {
      messageTemplate: campaignsToEdit.find(c => c.category === category).messageTemplate
    }

    if (isCustomLinkEnabled) {
      details.customUrl = customLink
    }

    campaignApi
      .update({
        subsidiaryId: DEFAULT_SUBSIDIARY_ID,
        categories: [category],
        details
      })
      .then(() => {
        history.push(`/publish-campaign/${category}`)
      })
  }

  function handleContentTabClick(tab) {
    if (tab === inviteContentTabs.email) {
      const showedPopup = promptEmailSubscriptionUpgrade({
        onDismiss: () => setActiveContentTab(inviteContentTabs.email)
      })

      if (showedPopup) {
        return
      }
    }

    setActiveContentTab(tab)
  }

  return (
    <div className={styles.editTextHolder} ref={wrapperRef}>
      {isSpamCheckDialogVisible && (
        <ConfirmationDialog
          onClose={() => setIsSpamCheckDialogVisible(false)}
          title={
            isLoadingSpamCheckResults
              ? 'Testing...'
              : spamResults.isSpamPredicted
              ? 'Spam'
              : 'Not spam'
          }
          hasConfirmButton={!isLoadingSpamCheckResults}
        >
          {isLoadingSpamCheckResults && <LoadingIndicator />}
          {!isLoadingSpamCheckResults && (
            <>
              <p>{spamResults.numberOfExperiments} experiments conducted</p>
              <p>
                Results: {spamResults.numberOfSpamPredicted} spam,{' '}
                {spamResults.numberOfNoSpamPredicted} not spam
              </p>
              <p>Spam rate: {spamResults.spamRate}</p>
            </>
          )}
        </ConfirmationDialog>
      )}
      {isEditHistoryVisible && (
        <CampaignHistory
          contentId={contentIdToShowHistory}
          category={categoryToShowHistory}
          onClose={() => setIsEditHistoryVisible(false)}
          isDefaultCampaigns={isDefaultCampaigns}
        />
      )}
      <CustomHeader title={title} width={950} />
      <div
        className={
          showEmailTab && activeContentTab === inviteContentTabs.email
            ? `${styles.editTextWrapper} ${styles.editTextWrapperEmailTab}`
            : styles.editTextWrapper
        }
      >
        {showEmailTab && (
          <div className={styles.contentTabsBar}>
            <button
              type="button"
              className={
                activeContentTab === inviteContentTabs.text
                  ? styles.contentTabActive
                  : styles.contentTab
              }
              onClick={() => handleContentTabClick(inviteContentTabs.text)}
            >
              Text
            </button>
            <button
              type="button"
              className={
                activeContentTab === inviteContentTabs.email
                  ? styles.contentTabActive
                  : styles.contentTab
              }
              onClick={() => handleContentTabClick(inviteContentTabs.email)}
            >
              <span className={styles.contentTabLabel}>
                Email
                <NewBadge className={styles.contentTabNewBadge}>BETA</NewBadge>
              </span>
            </button>
          </div>
        )}
        <div className={styles.editTextContent}>
          {showEmailTab && activeContentTab === inviteContentTabs.email && (
            hasEmailService ? (
              emailCampaignVariants.map(campaign => (
                <EmailEditorProvider
                  key={campaign.contentId}
                  contentId={campaign.contentId}
                  contentCategory={campaign.category}
                  mode="campaignBody"
                >
                  <CampaignEmailVariantSection
                    campaign={campaign}
                    isFocused={focusedEmailContentId === campaign.contentId}
                    previewPortalTarget={emailPreviewPortalTarget}
                    onFocus={() => setFocusedEmailContentId(campaign.contentId)}
                    onRegister={registerEmailEditor}
                  />
                </EmailEditorProvider>
              ))
            ) : (
              <p className={styles.emailTabLockedMessage}>
                Email editing requires the Email add-on. You can preview this tab, but saving
                email content is unavailable until Email is enabled for your property.
              </p>
            )
          )}
          {(!showEmailTab || activeContentTab === inviteContentTabs.text) &&
            campaignsToEdit.map((campaign, index) => (
            <div className={styles.editTextForm} key={campaign.contentId}>
              <p className={styles.editTextFormTitle}>
                {getCampaignTitle(campaign)}

                {isInternalAdmin && (
                  <button
                    className={styles.showHistoryButton}
                    onClick={() => handleClickShowHistory(category, campaign.contentId)}
                  >
                    History
                  </button>
                )}
                {isInternalAdmin && (
                  <button
                    className={styles.spamTestButton}
                    onClick={() =>
                      checkForSpam({
                        messageTemplate: campaign.messageTemplate,
                        hasMedia: false,
                        category: campaign.category
                      })
                    }
                  >
                    Spam check
                  </button>
                )}
                {canBePublished && (
                  <button
                    className={styles.publishButton}
                    onClick={() => goToPublish(campaign.category)}
                  >
                    Publish
                  </button>
                )}
              </p>{' '}
              <TextArea
                key={campaign.contentId}
                handleChange={text => handleCampaignChange(campaign.contentId, text)}
                handleUpdateUsedTags={tags =>
                  handleUpdateCampaignTags(campaign.contentId, tags)
                }
                disabled={!isInternalAdmin && campaign.type === 'consentRequest'}
                value={campaign.messageTemplate}
                baseTags={tags}
                requiredTagIds={requiredTagIds}
                optionalTagIds={optionalTagIds}
                shouldShowOptOutText={hasOptOutText}
                isInternalAdmin={isInternalAdmin}
                onFocus={() => setFocusedCampaignId(campaign.contentId)}
                autoFocus={
                  !isInternalAdmin &&
                  campaignsToEdit.some(c => c.type === 'consentRequest')
                    ? index === 1
                    : index === 0
                }
              />
            </div>
          ))}
          {(!showEmailTab || activeContentTab === inviteContentTabs.text) && canHaveImages && (
            <div className={styles.mediaUploaderHolder}>
              <MediaUploader
                selectedMedia={campaignMedia}
                setSelectedMedia={setCampaignMedia}
                removeSelectedMedia={() => setCampaignMedia(null)}
                mediaTag={mediaTag}
                acceptedFileTypes={[
                  'image/*',
                  'application/pdf',
                  'application/rtf',
                  'text/calendar',
                  'text/vcard',
                  'video/*'
                ]}
              />
            </div>
          )}
          {(!showEmailTab || activeContentTab === inviteContentTabs.text) && children}

          {(!showEmailTab || activeContentTab === inviteContentTabs.text) &&
            (hasCustomLinkControl || hasReviewLinkControl) && (
            <CampaignCustomUrl
              isCustomLinkEnabled={isCustomLinkEnabled}
              handleChangeCustomLinkToggle={handleChangeCustomLinkToggle}
              customLink={customLink}
              setCustomLink={setCustomLink}
              isCustomLinkValid={isCustomLinkValid}
              category={category}
            />
          )}
        </div>
        {showValidationErrors &&
          campaignsToEdit.map(c => (
            <>
              {c.remainingTags.length > 0 && (
                <div className={styles.validationErrorContainer}>
                  <InfoIcon className={styles.warnIcon} fill="red" />
                  <div className={styles.validationError}>
                    <span>Use </span>
                    <div className={styles.remainingTags}>
                      {c.remainingTags.map((t, i) => (
                        <>
                          {c.remainingTags.length > 1 &&
                          i === c.remainingTags.length - 1 ? (
                            <span> and </span>
                          ) : (
                            i > 0 && <span> , </span>
                          )}{' '}
                          <span className={styles.requiredTag}>{t.display}</span>
                        </>
                      ))}
                    </div>
                    <span> in </span>
                    <span>the </span>
                    <span className={styles.validationCampaignName}>
                      {getCampaignTitle(c)}{' '}
                    </span>
                    <span>field </span>
                    <span>to </span>
                    <span>save.</span>
                  </div>
                </div>
              )}
            </>
          ))}
        {showValidationErrors && isCustomLinkEnabled && !isCustomLinkValid && (
          <div className={styles.validationErrorContainer}>
            <InfoIcon className={styles.warnIcon} fill="red" />
            <div className={styles.validationError}>
              <span>Enter a valid link starting with http or https</span>
            </div>
          </div>
        )}
        <div className={styles.buttonHolder}>
          <Button type="secondary" onClick={history.goBack}>
            Discard
          </Button>
          <Button onClick={saveContent}>Save</Button>
        </div>
      </div>
      {showEmailTab && activeContentTab === inviteContentTabs.email ? (
        hasEmailService ? (
          <div
            ref={setEmailPreviewPortalTarget}
            className={styles.emailPreviewAside}
          />
        ) : (
          <div className={styles.emailPreviewAside}>
            <LockedEmailTemplatePreview />
          </div>
        )
      ) : (
        <TextPreview>
          <TextPreviewBubble
            defaultData={defaultData}
            hasOptOutText={hasOptOutText}
            hasLink={hasLink}
            preferShortLink={hasLink}
            customLink={isCustomLinkEnabled && customLink ? customLink : null}
            text={
              campaignsToEdit.find(c => c.contentId === focusedCampaignId).messageTemplate
            }
          />
          {campaignMedia && isCampaignImageVisible(campaignMedia) && (
            <>
              <FilePreview
                src={getImgUrlFromCDN(campaignMedia, mediaTag, hotelId)}
                fileName={campaignMedia}
                extension={getExtensionFromUrl(campaignMedia)}
                clickable={false}
                className={styles.campaignMedia}
                showFileName={true}
              />
            </>
          )}
        </TextPreview>
      )}
    </div>
  )
}

function EditCampaignContentEmailTabs(props) {
  const { hasEmailService } = useEmailSubscriptionGate()
  const [activeContentTab, setActiveContentTab] = useState(inviteContentTabs.text)

  function handleActiveContentTabChange(tab) {
    setActiveContentTab(tab)
  }

  return (
    <EditCampaignContentInner
      {...props}
      showEmailTab
      hasEmailService={hasEmailService}
      activeContentTab={activeContentTab}
      setActiveContentTab={handleActiveContentTabChange}
    />
  )
}

function EditCampaignContent(props) {
  const showEmailTab = shouldShowInviteEmailContentTab({
    type: props.campaignType,
    category: props.category
  })

  if (showEmailTab) {
    return <EditCampaignContentEmailTabs {...props} />
  }

  return <EditCampaignContentInner {...props} />
}

export default EditCampaignContent
export { shouldShowInviteEmailContentTab }
